// 1) Función que devuelve el último día hábil antes de la fecha dada
import { Dayjs } from 'dayjs';
import { conns } from '@cfg/axiosConfig.tsx';
import { logError, logInfo } from '@tools/LogsHandler.tsx';
import { OptionType } from '@tools/types/commonFields.types.ts';
import { message, notification } from 'antd';
import { RcFile } from 'antd/es/upload';
import { DEFAULT_CURRENCY } from '@cfg/defines.ts';
import { searchUsers } from '@tools/search/userSearch.ts';


export const getPreviousBusinessDay = (date: Dayjs): Dayjs => {
  const dow = date.day(); // 0=domingo, 1=lunes, …, 6=sábado
  if (dow === 0)   // hoy domingo   → retrocede 2 días (viernes)
    return date.subtract(2, 'day');
  if (dow === 1)   // hoy lunes     → retrocede 3 días (viernes)
    return date.subtract(3, 'day');
  // cualquier otro día → retrocede 1 día
  return date.subtract(1, 'day');
}


export interface DictValues { [key: string]: string | undefined }


/**
 * Función para obtener permisos.
 */
export const setUserPermission = async (url_name: string, cacheKey: string = 'permisos') => {
  // Obtener permisos del backend
  const response = await conns(url_name).post();
  const permisos = await response.data;

  // Almacenar permisos en el almacenamiento local
  localStorage.setItem(cacheKey, JSON.stringify(permisos));

  logInfo('Se recibieron y guardaron permisos: ', permisos);
};


/**
 * Función para verificar permisos del usuario
 * @param permission permiso a verificar.
 * @param cacheKey : [default: 'permisos'] nombre de la variable con la que se guardan los permisos.
 */
export const userHasPermission = (permission: string, cacheKey: string = 'permisos') => {
  // Obtén los permisos del almacenamiento local
  const permisos = JSON.parse(localStorage.getItem(cacheKey) || '[]');

  // logInfo('permission: ', permission, ' permisos: ', permisos, 'permisos.includes(permission): ', permisos.includes(permission));
  return permisos.includes(permission);
};


/**
 * Funciones de Busqueda
 */

/**
 * Normaliza un texto eliminando acentos y otros signos especiales.
 * @param text
 */
export const normalizeText = (text: string): string => {
  return (text ?? '')
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]+/g, "");
};

export const slugify = (text: string): string => {
  return text
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
};

export const cleanText = (value?: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed || null;
};

export const formatNumber = (value: number): string => (
  Number.isInteger(value) ? String(value) : String(Number(value.toFixed(2)))
);

/**
 * Función de Filtrado por palabras, independiente del orden.
 * @param input
 * @param option
 */
export const filterOption = (input: string, option: any) => {
  const rawText = option?.label ?? option?.children ?? option?.value ?? '';
  return searchUsers(
    [option],
    {
      query: input,
      fields: [{ key: 'label', getValue: () => rawText }],
    },
  ).length > 0;
};



export const getValidateField = (value: string | number, selectOptions: OptionType[]): string | number | boolean | null | undefined => {
  const normalizedValue = typeof value === 'string' ? normalizeText(value) : value;
  // logInfo('**** --- getValidateField - value: ', value, 'normalizedValue.toString(): ', normalizedValue.toString());
  const foundOption = selectOptions.find(option =>
    normalizeText(option.label.toString()) === normalizedValue.toString()
  );
  return foundOption ? foundOption.value : undefined;
};

/**
 * Devuelve la opción coincidente (label + value) o ambos indefinidos si no hay match.
 *
 * @param value         Valor a buscar (puede venir como label o value real).
 * @param selectOptions Opciones del select.
 */
export const getLabelValidateField = (
  value: string | number,
  selectOptions: OptionType[],
): { label: string | undefined; value: string | number | boolean | null | undefined } => {
  /* ─── Guard clauses ───────────────────────────────────────── */
  if (value === undefined || value === null) return { label: undefined, value: undefined };
  if (typeof value === 'string' && value.trim() === '') return { label: undefined, value: undefined };

  /* ─── Normalización para comparar ─────────────────────────── */
  const normalizedValue = typeof value === 'string' ? normalizeText(value) : value;

  /* ─── Búsqueda por label normalizado ──────────────────────── */
  const foundOption = selectOptions.find(option =>
    normalizeText(option.label.toString()) === normalizedValue.toString()
  );
  return foundOption ? {label: normalizedValue.toString(), value: foundOption.value} : {label: undefined, value: undefined};
};


/**
 * Función para obtener el nombre de un elemento a partir del ID
 * @param id
 * @param array
 * @param key
 */
export const getValueById = <T extends { id: number | string }, K extends keyof T>(id: number | string, array: T[], key: K = 'nombre' as K): string => {
  const res = array.find(elem => String(elem.id) === String(id));
  return res ? String(res[key]) : '-';
};


/**
 * Funciones para manejar valores responsive para componentes Ant Design
 * @param values
 */// Definición de los puntos de quiebre
type Breakpoints = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

const breakpoints: Record<Breakpoints, number> = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1400,
};

const getCurrentBreakpoint = (): Breakpoints => {
  const width = window.innerWidth;
  if (width < breakpoints.sm) return 'xs';
  if (width < breakpoints.md) return 'sm';
  if (width < breakpoints.lg) return 'md';
  if (width < breakpoints.xl) return 'lg';
  if (width < breakpoints.xxl) return 'xl';
  return 'xxl';
  // if (width >= breakpoints.xxl) return 'xxl';
  // if (width >= breakpoints.xl) return 'xl';
  // if (width >= breakpoints.lg) return 'lg';
  // if (width >= breakpoints.md) return 'md';
  // if (width >= breakpoints.sm) return 'sm';
  // return 'xs';
};

interface ResponsiveValues<T> {
  xs?: T;
  sm?: T;
  md?: T;
  lg?: T;
  xl?: T;
  xxl?: T;
}

const getPreviousDefinedValue = <T, >(values: ResponsiveValues<T>, breakpoint: Breakpoints): T | undefined => {
  const order: Breakpoints[] = ['xxl', 'xl', 'lg', 'md', 'sm', 'xs'];
  const startIndex = order.indexOf(breakpoint);
  logInfo('getPreviousDefinedValue - startIndex: ', startIndex);
  for (let i = startIndex; i >= 0; i--) {
    if (values[order[i]] !== undefined) {
      return values[order[i]];
    }
  }
  return undefined;
};

export const getMediaResponsiveValues = <T, >(values: ResponsiveValues<T>): T => {
  const currentBreakpoint = getCurrentBreakpoint();
  const value = getPreviousDefinedValue(values, currentBreakpoint);
  if (value !== undefined) {
    return value;
  } else {
    throw new Error("No value defined for any breakpoint.");
    // return values?[0] : 100;
  }
};


// Acepta number o string (e.g. "1.234,56", "$ 1.234", "1234.56", "- 2.500,0")
export const getFormattedPrice = (
  price: number | string | null | undefined,
  currency: string | null = DEFAULT_CURRENCY,
  {
    locale = 'es-AR',
    fallback = '-',
    minimumFractionDigits = 0,
    maximumFractionDigits = 2,
  }: {
    locale?: string;
    fallback?: string;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  } = {}
): string => {
  const toNumber = (v: number | string | null | undefined): number | null => {
    if (typeof v === 'number') return Number.isFinite(v) ? v : null;
    if (typeof v !== 'string') return null;

    const raw = v.trim();
    if (!raw) return null;

    // Dejar solo dígitos, separadores y signo
    const cleaned = raw.replace(/[^\d,.\-]/g, '');

    const hasComma = cleaned.includes(',');
    const hasDot = cleaned.includes('.');
    const lastComma = cleaned.lastIndexOf(',');
    const lastDot = cleaned.lastIndexOf('.');

    let normalized = cleaned;

    if (hasComma && hasDot) {
      // Si hay ambos, el último separador suele ser el decimal.
      normalized = lastComma > lastDot
        ? cleaned.replace(/\./g, '').replace(',', '.') // coma decimal
        : cleaned.replace(/,/g, '');// punto decimal
    } else if (hasComma) {
      // Solo coma: interpretar como decimal (es-AR)
      normalized = cleaned.replace(/\./g, '').replace(',', '.');
    } else if (hasDot) {
      // Solo punto: ¿miles o decimal?
      const dotParts = cleaned.split('.');
      if (dotParts.length > 2 || (dotParts.length === 2 && dotParts[1].length === 3)) {
        normalized = cleaned.replace(/\./g, '');
      } else {
        // Probable decimal estilo en-US: "1234.56"
        normalized = cleaned.replace(/,/g, '');
      }
    }

    const n = Number(normalized);
    return Number.isFinite(n) ? n : null;
  };

  const n = toNumber(price);
  if (n === null) return fallback;

  try {
    // ➜ Cambio mínimo: sólo usamos estilo "currency" si hay currency; si es null, formato numérico simple.
    const options: Intl.NumberFormatOptions = currency
      ? { style: 'currency', currency, minimumFractionDigits, maximumFractionDigits }
      : { minimumFractionDigits, maximumFractionDigits };

    return new Intl.NumberFormat(locale, options).format(n);
  } catch {
    // Respaldo si la moneda/locale es inválida
    const digits = Math.max(0, Math.min(20, maximumFractionDigits));
    return n.toFixed(digits);
  }
};


export interface ParsedPrice {
  value: number;
  currency: string;
}

/**
 * Normaliza la estructura de precio recibida desde la API.
 * El backend expone los importes en un modelo `ItemPrice` donde el campo
 * principal es `value` (entero) y opcionalmente puede venir acompañado de
 * información de moneda e impuestos. Para mantener compatibilidad con versiones
 * previas también soportamos objetos con `incl_tax`/`excl_tax` o un número
 * plano.
 */
export const parsePrice = (
  price: unknown,
  defaultCurrency = 'ARS',
): ParsedPrice | null => {
  if (price === null || price === undefined) return null;

  const toNumber = (v: unknown): number | null => {
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string') {
      const s = v.trim();
      if (!s) return null;
      const n = Number(s);
      return Number.isFinite(n) ? n : null;
    }
    return null;
  };

  const normalizeCurrency = (c: unknown): string =>
    typeof c === 'string' && c.trim()
      ? c.trim().toUpperCase()
      : defaultCurrency;

  // Caso: viene como número o string directo
  if (typeof price === 'number' || typeof price === 'string') {
    const n = toNumber(price);
    return n == null ? null : { value: Math.max(0, n), currency: defaultCurrency };
  }

  // Caso: objeto con distintos “shapes” aceptados
  if (typeof price === 'object') {
    const p = price as Record<string, unknown>;
    const n = toNumber(p.value ?? p.incl_tax ?? p.price);
    if (n == null) return null;

    const currency = normalizeCurrency(p.currency);
    return { value: Math.max(0, n), currency };
  }

  return null;
};


export const stripHtml = (html: string) => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  return tempDiv.innerText;
};

const fallbackCopyToClipboard = (text: string) => {
  const textArea = document.createElement("textarea");
  textArea.value = text;
  // Evitar que el elemento textarea sea visible y parte de la página
  textArea.style.position = "fixed";
  textArea.style.top = "0";
  textArea.style.left = "0";
  textArea.style.width = "2em";
  textArea.style.height = "2em";
  textArea.style.padding = "0";
  textArea.style.border = "none";
  textArea.style.outline = "none";
  textArea.style.boxShadow = "none";
  textArea.style.background = "transparent";
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();
  try {
    document.execCommand('copy');
    message.success('URL copiada al portapapeles');
  } catch (err) {
    logError('fallbackCopyToClipboard - err: ', err);
    message.error('Error al copiar la URL');
  }
  document.body.removeChild(textArea);
};
export const copyToClipboard = async (text: string) => {
  if (!navigator.clipboard) {
    logError('Clipboard API not supported');
    fallbackCopyToClipboard(text);
    return;
  }

  try {
    await navigator.clipboard.writeText(text);
    message.success('URL copiada al portapapeles');
  } catch (err) {
    logError('copyToClipboard - err: ', err);
    message.error('Error al copiar la URL');
  }
};

export const handleOptionsSort = (optionA: OptionType, optionB: OptionType) => {
  const labelA = typeof optionA.label === 'string' ? optionA.label.toLowerCase() : '';
  const labelB = typeof optionB.label === 'string' ? optionB.label.toLowerCase() : '';
  return labelA.localeCompare(labelB);
};

// export const filterOption = (input: string, option: any) => {
//     if (typeof option?.label === 'string') {
//         return option.label.toLowerCase().includes(input.toLowerCase());
//     }
//     return false;
// };


export const extractYoutubeEmbedLink = (iframeString: string) => {
  const regex = /src="([^"]+)"/;
  const match = iframeString.match(regex);
  return match ? match[1] : null;
};


export const validateFileType = (file: RcFile, allowedTypes: string[]) => {
  logInfo('validateFileType inicia ');
  const isAllowed = allowedTypes.some((type) => {
    logInfo('validateFileType - allowedTypes: ', allowedTypes, 'type: ', type, 'file.type: ', file.type);
    if (type === '*') { return true; }
    if (type.endsWith('/*')) {
      return file.type.startsWith(type.slice(0, -1));
    }
    return file.type === type;
  });
  if (!isAllowed) {
    notification.error({
      message: 'Error de carga',
      description: `Tipo de archivo no permitido: ${file.type}`,
      placement: 'top',
      duration: 10,
    });
  }
  return isAllowed;
};


export const openNotificationWithIcon = (type: 'success' | 'error', message: string, description: string, btn?:React.ReactNode) => {
  notification[type]({
    message,
    description,
    btn
  });
};


export const isElementVisible = (
  {element, checkInViewport = false, fullyInView = false}
  : {
    element: HTMLElement | null,
    checkInViewport?: boolean,  // Parámetro para verificar si está en el viewport
    fullyInView?: boolean       // Parámetro para verificar si el elemento debe estar completamente visible
  }
): boolean => {
  if (!element) return false;
  // if(checkInViewport){
  //     logInfo('isElementVisible element: ', element, 'checkInViewport: ', checkInViewport, 'fullyInView: ', fullyInView);
  // }

  const rect = element.getBoundingClientRect();
  const hasDimensions = rect.width > 0 && rect.height > 0;
  const isInFlow = element.offsetParent !== null;

  // Verificar si tiene dimensiones y está en el flujo del documento
  if (!hasDimensions || !isInFlow) {
    return false;
  }

  // Si no se solicita verificar el viewport, retorna true
  if (!checkInViewport) {
    return true;
  }

  // logInfo('Verificar si el elemento está dentro del área visible del viewport fullyInView: ', fullyInView);
  // Verificar si el elemento está dentro del área visible del viewport
  const inViewport = fullyInView
    ? rect.top >= 0 && rect.left >= 0 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) && rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    : rect.top < (window.innerHeight || document.documentElement.clientHeight) && rect.bottom > 0 && rect.left < (window.innerWidth || document.documentElement.clientWidth) && rect.right > 0;

  return inViewport;
};


export const waitForElementToBeVisible = (
  {element, timeout = 5000, fullyInView = false}:
  {
    element: HTMLElement | null,
    timeout?: number,
    fullyInView?: boolean,
  }): Promise<HTMLElement | null> => {
  return new Promise((resolve) => {
    if (isElementVisible({element, checkInViewport: true, fullyInView})) {
      resolve(element);
      return;
    }

    const observer = new MutationObserver(() => {
      if (isElementVisible({element, checkInViewport: true, fullyInView})) {
        logInfo('Acá ya recibió isElementVisible: TRUE');
        observer.disconnect();
        resolve(element);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    const intervalId = setInterval(() => {
      if (isElementVisible({element, checkInViewport: true, fullyInView})) {
        logInfo('Visibilidad detectada con setInterval');
        observer.disconnect();
        clearInterval(intervalId); // Detener el chequeo
        resolve(element);
      }
    }, 150); // Verificar cada 100ms

    // Timeout en caso de que el elemento nunca sea visible
    setTimeout(() => {
      observer.disconnect();
      clearInterval(intervalId); // Limpiar el intervalo
      resolve(null); // Si no se encuentra el elemento, devolver `null`
    }, timeout);
  });
};

export const waitForScrollToComplete = (element: HTMLElement): Promise<void> => {
  return new Promise((resolve) => {
    if (isElementVisible({element, checkInViewport: true, fullyInView: true})){
      // Si el elemento ya se encuentra visible, resuelvo la promesa antes de hacer el scroll.
      resolve(); // Resolver la promesa
      return;
    }
    const intervalStep = 150
    let previousScrollY = window.scrollY;
    let interval: number | null = null;

    // Capturar el tiempo de inicio
    const startTime = performance.now();

    const checkScrollPosition = () => {
      const currentScrollY = window.scrollY;

      // Si la posición de scroll sigue cambiando, actualizar previousScrollY
      if (currentScrollY !== previousScrollY) {
        previousScrollY = currentScrollY;
      } else {
        // Si la posición no cambia, el scroll ha terminado
        clearInterval(interval as number);
        window.removeEventListener('scroll', handleScroll); // Eliminar el listener de scroll

        const endTime = performance.now(); // Capturar el tiempo de finalización
        const extraTime = (endTime - startTime) * 1.2;
        logInfo('::::::: ExtraTime: ', extraTime);
        // Esperar 100 ms antes de comenzar la comprobación para asegurar que el scroll inicia
        setTimeout(() => {
          resolve(); // Resolver la promesa
        }, extraTime);
      }
    };

    const handleScroll = () => {
      // Iniciar el intervalo que verifica la posición cada 100 ms
      interval = window.setInterval(checkScrollPosition, intervalStep);
    };

    // Iniciar el scroll hacia el elemento
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'center',
    });

    // Esperar 150 ms antes de comenzar la comprobación para asegurar que el scroll inicia
    setTimeout(() => {
      window.addEventListener('scroll', handleScroll);
      handleScroll(); // Empezar a escuchar los eventos de scroll
    }, intervalStep);
  });
};
